const IAP_NOTICE_EVENT = "invown:iap-session-expired";
const IAP_RETURN_PATH_KEY = "invown:iap-return-path";
const IAP_GENERATED_HEADER = "x-goog-iap-generated-response";

let noticeDispatched = false;

function responseHeader(response, name) {
  if (!response || !response.headers) {
    return "";
  }
  if (typeof response.headers.get === "function") {
    return response.headers.get(name) || "";
  }
  return response.headers[name] || response.headers[name.toLowerCase()] || "";
}

export function currentReturnPath(locationObject = window.location) {
  return `${locationObject.pathname || "/"}${locationObject.search || ""}${
    locationObject.hash || ""
  }`;
}

export function isIapLoginUrl(value, origin = window.location.origin) {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value, origin);
    if (parsed.origin === origin) {
      return false;
    }
    return (
      parsed.hostname === "accounts.google.com" ||
      parsed.hostname === "iap.googleapis.com"
    );
  } catch (_error) {
    return false;
  }
}

export function isIapSessionFailure(error, origin = window.location.origin) {
  if (error && error._invownIapSessionExpired === true) {
    return true;
  }

  const response = error && error.response;
  const generated = String(responseHeader(response, IAP_GENERATED_HEADER)).toLowerCase();
  if (response && response.status === 401 && ["true", "1", "yes"].includes(generated)) {
    return true;
  }

  const responseUrl =
    (response && response.request && response.request.responseURL) ||
    (error && error.request && error.request.responseURL) ||
    (response && response.headers &&
      (response.headers.location || response.headers.Location));
  return isIapLoginUrl(responseUrl, origin);
}

export function withIapAjaxMarker(config, origin = window.location.origin) {
  if (!config || !config.url) {
    return config;
  }

  let requestUrl;
  try {
    requestUrl = new URL(config.url, origin);
  } catch (_error) {
    return config;
  }
  if (requestUrl.origin !== origin) {
    return config;
  }

  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      "X-Requested-With": "XMLHttpRequest",
    },
  };
}

export function notifyIapSessionExpired(error, locationObject = window.location) {
  if (error && typeof error === "object") {
    error._invownIapSessionExpired = true;
  }
  const returnPath = currentReturnPath(locationObject);
  try {
    window.sessionStorage.setItem(IAP_RETURN_PATH_KEY, returnPath);
  } catch (_storageError) {
    // The in-memory event still provides a safe same-origin return path.
  }

  if (!noticeDispatched) {
    noticeDispatched = true;
    window.dispatchEvent(
      new CustomEvent(IAP_NOTICE_EVENT, { detail: { returnPath } })
    );
  }
  return error;
}

export function handleIapAxiosFailure(error) {
  if (!isIapSessionFailure(error)) {
    return false;
  }
  notifyIapSessionExpired(error);
  return true;
}

export async function probeIapSession(fetchImpl) {
  const request =
    fetchImpl ||
    (typeof window.fetch === "function" ? window.fetch.bind(window) : null);
  if (!request) {
    return false;
  }
  try {
    const response = await request("/api/healthz", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      redirect: "manual",
    });
    const generated = String(responseHeader(response, IAP_GENERATED_HEADER)).toLowerCase();
    const expired =
      response.type === "opaqueredirect" ||
      (response.status === 401 && ["true", "1", "yes"].includes(generated)) ||
      isIapLoginUrl(response.url);
    if (expired) {
      notifyIapSessionExpired(new Error("Secure environment session expired."));
    }
    return expired;
  } catch (_error) {
    // A generic network failure is not enough evidence to declare IAP expiry.
    return false;
  }
}

export function resumeIapSession(locationObject = window.location) {
  let returnPath = currentReturnPath(locationObject);
  try {
    const stored = window.sessionStorage.getItem(IAP_RETURN_PATH_KEY);
    if (stored && stored.startsWith("/") && !stored.startsWith("//")) {
      returnPath = stored;
    }
  } catch (_storageError) {
    // Continue with the current route.
  }
  locationObject.assign(returnPath);
}

export function resetIapNoticeForTests() {
  noticeDispatched = false;
}

export { IAP_NOTICE_EVENT };
